// pages/Dashboard/DashboardPage.tsx
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
} from '@mui/material';
import {
  Assignment,
  BarChart,
  AccountTree,
  TrendingUp,
  CheckCircle,
  Schedule,
} from '@mui/icons-material';
import { useAppSelector } from '../../hooks/redux';

const DashboardPage: React.FC = () => {
  const { user, organization } = useAppSelector(state => state.auth);


  console.log('=== USER DATA ===');
  console.log('User:', user);
  console.log('User groups:', user?.userGroups);
  console.log('User metadata:', user?.metaData);
  console.log('User metadata raw:', user?.metaDataRaw);
  if (user?.metaData) {
    console.log('Imię:', user.metaData.Imie);
    console.log('Hala:', user.metaData.Hala);
    console.log('Stanowisko:', user.metaData.Stanowisko);
  }
  console.log('==================');

  // Mock data - w prawdziwej aplikacji z Redux/API
  const stats = {
    pendingForms: 5,
    completedToday: 12,
    totalObjects: 24,
    activeReports: 8,
  };

  const recentActivity = [
    {
      id: 1,
      action: 'Wypełniono formularz "Kontrola jakości"',
      object: 'Linia 1 - Maszyna A',
      time: '10 min temu',
      status: 'completed',
    },
    {
      id: 2,
      action: 'Wygenerowano raport "Miesięczny przegląd"',
      object: 'Oddział Północny',
      time: '1 godz. temu',
      status: 'completed',
    },
    {
      id: 3,
      action: 'Formularz "Inspekcja bezpieczeństwa" oczekuje',
      object: 'Hala B - Sektor 3',
      time: '2 godz. temu',
      status: 'pending',
    },
  ];

  const quickActions = [
    {
      title: 'Skanuj QR',
      description: 'Przejdź do obiektu przez kod QR',
      icon: <AccountTree />,
      action: () => console.log('Open QR Scanner'),
    },
    {
      title: 'Nowy formularz',
      description: 'Wypełnij formularz dla obiektu',
      icon: <Assignment />,
      action: () => console.log('New Form'),
    },
    {
      title: 'Zobacz raporty',
      description: 'Przeglądaj dostępne raporty',
      icon: <BarChart />,
      action: () => console.log('View Reports'),
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Witaj, {user?.username}!
      </Typography>

      {/* Statystyki */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { 
          xs: '1fr', 
          sm: '1fr 1fr', 
          md: '1fr 1fr 1fr 1fr' 
        }, 
        gap: 3, 
        mt: 3 
      }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Schedule color="warning" sx={{ mr: 2 }} />
              <Box>
                <Typography variant="h4" color="warning.main">
                  {stats.pendingForms}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Oczekujące formularze
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CheckCircle color="success" sx={{ mr: 2 }} />
              <Box>
                <Typography variant="h4" color="success.main">
                  {stats.completedToday}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Dzisiaj ukończone
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AccountTree color="primary" sx={{ mr: 2 }} />
              <Box>
                <Typography variant="h4" color="primary.main">
                  {stats.totalObjects}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Dostępne obiekty
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TrendingUp color="info" sx={{ mr: 2 }} />
              <Box>
                <Typography variant="h4" color="info.main">
                  {stats.activeReports}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Aktywne raporty
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Główna zawartość */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, 
        gap: 3, 
        mt: 3 
      }}>
        {/* Ostatnia aktywność */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Ostatnia aktywność
            </Typography>
            <List>
              {recentActivity.map((activity) => (
                <ListItem key={activity.id}>
                  <ListItemIcon>
                    {activity.status === 'completed' ? (
                      <CheckCircle color="success" />
                    ) : (
                      <Schedule color="warning" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={activity.action}
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography variant="caption">
                          {activity.object}
                        </Typography>
                        <Chip
                          label={activity.status === 'completed' ? 'Ukończone' : 'Oczekuje'}
                          size="small"
                          color={activity.status === 'completed' ? 'success' : 'warning'}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {activity.time}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>

        {/* Szybkie akcje */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Szybkie aaaaakcje
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {quickActions.map((action, index) => (
                <Button
                  key={index}
                  variant="outlined"
                  startIcon={action.icon}
                  onClick={action.action}
                  sx={{
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    p: 2,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2">
                      {action.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {action.description}
                    </Typography>
                  </Box>
                </Button>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default DashboardPage;